import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  KeyRound,
  Loader2,
  RefreshCw,
  ServerCog,
  ShieldCheck,
  Trash2,
  Webhook
} from 'lucide-react'
import { apiService } from '../../services/api'

interface ApiKey {
  id: string
  name: string
  prefix: string
  scopes: string[]
  status: string
  created_at?: string
  last_used_at?: string | null
  secret?: string
}

interface WebhookEndpoint {
  id: string
  name: string
  url: string
  events: string[]
  status: string
  secret_status: string
  created_at?: string
}

interface DeveloperSummary {
  organization_id: string
  api_keys: number
  active_api_keys: number
  webhooks: number
  active_webhooks: number
  scopes: string[]
  endpoints: {
    api: string
    openapi: string
    mcp_manifest: string
    mcp_call: string
  }
}

interface McpInfo {
  auth: string
  required_scopes: string[]
  optional_tool_scopes: string[]
  endpoints: {
    manifest: string
    call: string
  }
}

const defaultScopes = ['calls:read', 'calls:write', 'leads:read', 'leads:write', 'sms:read', 'sms:write', 'webhooks:read', 'webhooks:write', 'usage:read', 'mcp:use']

function getApiErrorMessage(error: unknown, fallback: string) {
  if (typeof error !== 'object' || error === null || !('response' in error)) return fallback
  const response = (error as { response?: { data?: { error?: unknown; message?: unknown } } }).response
  const message = response?.data?.error || response?.data?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}

function formatDate(value?: string | null) {
  if (!value) return 'Nunca'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value))
}

function StatusBadge({ value }: { value: string }) {
  const normalized = value.toLowerCase()
  const className =
    normalized.includes('active') || normalized.includes('ready') || normalized.includes('enabled')
      ? 'badge-success'
      : normalized.includes('paused') || normalized.includes('pending')
        ? 'badge-warning'
        : 'badge-danger'

  return <span className={className}>{value}</span>
}

function ScopeToggle({
  scope,
  enabled,
  onToggle
}: {
  scope: string
  enabled: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={[
        'inline-flex min-h-9 items-center rounded-lg border px-3 text-xs font-semibold transition',
        enabled
          ? 'border-primary-200 bg-primary-50 text-primary-800'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
      ].join(' ')}
    >
      {scope}
    </button>
  )
}

async function copyToClipboard(value: string, message: string) {
  if (!value) return
  await navigator.clipboard?.writeText(value)
  toast.success(message)
}

export default function Developer() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [summary, setSummary] = useState<DeveloperSummary | null>(null)
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([])
  const [mcp, setMcp] = useState<McpInfo | null>(null)
  const [latestApiSecret, setLatestApiSecret] = useState('')
  const [keyForm, setKeyForm] = useState({
    name: 'Chave operacional',
    scopes: ['calls:read', 'calls:write', 'leads:read', 'leads:write', 'usage:read', 'mcp:use']
  })
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    events: 'lead.created,call.completed,message.received',
    secret: ''
  })

  const scopeOptions = useMemo(() => summary?.scopes?.length ? summary.scopes : defaultScopes, [summary])

  async function refresh() {
    setLoading(true)
    try {
      const [summaryResponse, keysResponse, webhooksResponse, mcpResponse] = await Promise.all([
        apiService.getDeveloperSummary(),
        apiService.getDeveloperApiKeys(),
        apiService.getDeveloperWebhookEndpoints(),
        apiService.getDeveloperMcp()
      ])

      setSummary(summaryResponse.data.data as DeveloperSummary)
      setApiKeys(keysResponse.data.data as ApiKey[])
      setWebhooks(webhooksResponse.data.data as WebhookEndpoint[])
      setMcp(mcpResponse.data.data as McpInfo)
    } catch (error) {
      console.error('Falha ao carregar integrações:', error)
      toast.error(getApiErrorMessage(error, 'Não foi possível carregar integrações.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  async function runAction(action: () => Promise<unknown>, successMessage: string) {
    setSaving(true)
    try {
      await action()
      toast.success(successMessage)
      await refresh()
    } catch (error) {
      console.error('Falha em integrações:', error)
      toast.error(getApiErrorMessage(error, 'Ação de integração falhou.'))
    } finally {
      setSaving(false)
    }
  }

  function toggleScope(scope: string) {
    setKeyForm((current) => {
      const scopes = current.scopes.includes(scope)
        ? current.scopes.filter((item) => item !== scope)
        : [...current.scopes, scope]
      return { ...current, scopes }
    })
  }

  async function handleCreateApiKey() {
    if (!keyForm.scopes.length) {
      toast.error('Selecione ao menos um escopo.')
      return
    }

    setSaving(true)
    try {
      const response = await apiService.createDeveloperApiKey({
        name: keyForm.name.trim() || `Chave operacional ${new Date().toLocaleDateString('pt-BR')}`,
        scopes: keyForm.scopes
      })
      const created = response.data.data as ApiKey
      setLatestApiSecret(created.secret || '')
      toast.success('Chave de API criada.')
      await refresh()
    } catch (error) {
      console.error('Falha ao criar API key:', error)
      toast.error(getApiErrorMessage(error, 'Não foi possível criar a chave.'))
    } finally {
      setSaving(false)
    }
  }

  async function handleCreateWebhook() {
    if (!/^https?:\/\/.+/.test(webhookForm.url.trim())) {
      toast.error('URL inválida. Use https://seu-dominio.com/webhook')
      return
    }
    await runAction(async () => {
      await apiService.createDeveloperWebhookEndpoint({
        name: webhookForm.name,
        url: webhookForm.url,
        events: webhookForm.events
          .split(',')
          .map((eventName) => eventName.trim())
          .filter(Boolean),
        signing_secret: webhookForm.secret.trim() || undefined
      })
      setWebhookForm({ name: '', url: '', events: 'lead.created,call.completed,message.received', secret: '' })
    }, 'Webhook cadastrado.')
  }

  async function handleToggleWebhook(webhookEndpoint: WebhookEndpoint) {
    const nextStatus = webhookEndpoint.status === 'active' ? 'paused' : 'active'
    await runAction(
      () => apiService.updateDeveloperWebhookEndpoint(webhookEndpoint.id, { status: nextStatus }),
      nextStatus === 'active' ? 'Webhook ativado.' : 'Webhook pausado.'
    )
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Integrações</h1>
          <p className="page-description">
            API, webhooks e MCP ficam vinculados ao tenant atual e usam escopos por chave.
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={refresh} disabled={saving}>
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </button>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="metric-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500">Chaves ativas</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{summary?.active_api_keys ?? 0}</p>
              <p className="mt-1 text-xs font-medium text-slate-500">{summary?.api_keys ?? 0} cadastradas</p>
            </div>
            <div className="metric-icon bg-primary-50 text-primary-600">
              <KeyRound className="h-5 w-5" />
            </div>
          </div>
        </div>
        <div className="metric-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500">Webhooks ativos</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{summary?.active_webhooks ?? 0}</p>
              <p className="mt-1 text-xs font-medium text-slate-500">{summary?.webhooks ?? 0} cadastrados</p>
            </div>
            <div className="metric-icon bg-success-50 text-success-600">
              <Webhook className="h-5 w-5" />
            </div>
          </div>
        </div>
        <div className="metric-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500">MCP</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{mcp ? 'Ativo' : '—'}</p>
              <p className="mt-1 text-xs font-medium text-slate-500">Bearer + mcp:use</p>
            </div>
            <div className="metric-icon bg-indigo-50 text-indigo-600">
              <ServerCog className="h-5 w-5" />
            </div>
          </div>
        </div>
        <div className="metric-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-500">Escopos</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{scopeOptions.length}</p>
              <p className="mt-1 text-xs font-medium text-slate-500">Disponíveis</p>
            </div>
            <div className="metric-icon bg-warning-50 text-warning-600">
              <ShieldCheck className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        {/* API Keys */}
        <section className="surface">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Chaves de API</h2>
              <p className="panel-subtitle">Acesso externo por tenant e por escopo.</p>
            </div>
            <ShieldCheck className="h-5 w-5 text-slate-400" />
          </div>
          <div className="space-y-4 p-4">
            {/* Secret reveal */}
            {latestApiSecret && (
              <div className="rounded-lg border border-warning-200 bg-warning-50 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-warning-800">
                  <AlertTriangle className="h-4 w-4 flex-none" />
                  Copie agora — não será exibida novamente.
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <code className="min-w-0 flex-1 break-all rounded bg-white px-2 py-1.5 text-xs text-slate-900">
                    {latestApiSecret}
                  </code>
                  <button
                    type="button"
                    className="btn-secondary shrink-0"
                    onClick={() => copyToClipboard(latestApiSecret, 'Segredo copiado.')}
                  >
                    <Copy className="h-4 w-4" />
                    Copiar
                  </button>
                </div>
              </div>
            )}

            {/* Create form */}
            <div className="rounded-lg border border-slate-200 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Nova chave</p>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <input
                  className="input"
                  placeholder="Nome da chave"
                  value={keyForm.name}
                  onChange={(event) => setKeyForm({ ...keyForm, name: event.target.value })}
                />
                <button type="button" className="btn-primary" disabled={saving || !keyForm.scopes.length} onClick={handleCreateApiKey}>
                  <KeyRound className="h-4 w-4" />
                  Criar chave
                </button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {scopeOptions.map((scope) => (
                  <ScopeToggle key={scope} scope={scope} enabled={keyForm.scopes.includes(scope)} onToggle={() => toggleScope(scope)} />
                ))}
              </div>
            </div>

            {/* Keys list */}
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div key={key.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-slate-950">{key.name}</p>
                        <StatusBadge value={key.status} />
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        <span className="font-mono">{key.prefix}</span>
                        {' · '} último uso: {formatDate(key.last_used_at)}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {key.scopes.map((scope) => (
                          <span key={scope} className="badge badge-info">{scope}</span>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-danger shrink-0"
                      disabled={saving}
                      aria-label={`Revogar chave ${key.name}`}
                      onClick={() => runAction(() => apiService.revokeDeveloperApiKey(key.id), 'Chave revogada.')}
                    >
                      <Trash2 className="h-4 w-4" />
                      Revogar
                    </button>
                  </div>
                </div>
              ))}
              {!apiKeys.length && (
                <p className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">Nenhuma chave cadastrada.</p>
              )}
            </div>
          </div>
        </section>

        {/* Webhooks */}
        <section className="surface">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Webhooks</h2>
              <p className="panel-subtitle">Eventos enviados para sistemas externos.</p>
            </div>
            <Webhook className="h-5 w-5 text-slate-400" />
          </div>
          <div className="space-y-4 p-4">
            {/* Create form */}
            <div className="rounded-lg border border-slate-200 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Novo webhook</p>
              <div className="space-y-3">
                <input
                  className="input"
                  placeholder="Nome do webhook"
                  value={webhookForm.name}
                  onChange={(event) => setWebhookForm({ ...webhookForm, name: event.target.value })}
                />
                <input
                  className="input font-mono text-sm"
                  placeholder="https://cliente.com/webhook/aicall"
                  value={webhookForm.url}
                  onChange={(event) => setWebhookForm({ ...webhookForm, url: event.target.value })}
                />
                <input
                  className="input"
                  placeholder="Eventos separados por vírgula"
                  value={webhookForm.events}
                  onChange={(event) => setWebhookForm({ ...webhookForm, events: event.target.value })}
                />
                <input
                  className="input"
                  type="password"
                  placeholder="Segredo HMAC opcional"
                  value={webhookForm.secret}
                  onChange={(event) => setWebhookForm({ ...webhookForm, secret: event.target.value })}
                />
                <button type="button" className="btn-primary w-full" disabled={saving || !webhookForm.name || !webhookForm.url} onClick={handleCreateWebhook}>
                  <Webhook className="h-4 w-4" />
                  Criar webhook
                </button>
              </div>
            </div>

            {/* Webhooks list */}
            <div className="space-y-3">
              {webhooks.map((webhookEndpoint) => (
                <div key={webhookEndpoint.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-slate-950">{webhookEndpoint.name}</p>
                        <StatusBadge value={webhookEndpoint.status} />
                      </div>
                      <p className="mt-1 truncate font-mono text-xs text-slate-500">{webhookEndpoint.url}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {webhookEndpoint.events.map((event) => (
                      <span key={event} className="badge">{event}</span>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      className="btn-secondary flex-1"
                      disabled={saving}
                      aria-label={webhookEndpoint.status === 'active' ? `Pausar webhook ${webhookEndpoint.name}` : `Ativar webhook ${webhookEndpoint.name}`}
                      onClick={() => handleToggleWebhook(webhookEndpoint)}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {webhookEndpoint.status === 'active' ? 'Pausar' : 'Ativar'}
                    </button>
                    <button
                      type="button"
                      className="btn-danger flex-1"
                      disabled={saving}
                      aria-label={`Desativar webhook ${webhookEndpoint.name}`}
                      onClick={() => runAction(() => apiService.deleteDeveloperWebhookEndpoint(webhookEndpoint.id), 'Webhook desativado.')}
                    >
                      <Trash2 className="h-4 w-4" />
                      Desativar
                    </button>
                  </div>
                </div>
              ))}
              {!webhooks.length && (
                <p className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">Nenhum webhook cadastrado.</p>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* API & MCP Endpoints */}
      <section className="surface">
        <div className="panel-header">
          <div>
            <h2 className="panel-title">API pública e MCP</h2>
            <p className="panel-subtitle">Endpoints versionados para integrações externas.</p>
          </div>
          <ServerCog className="h-5 w-5 text-slate-400" />
        </div>
        <div className="p-4">
          <div className="grid gap-3 lg:grid-cols-2">
            {([
              ['API base', summary?.endpoints.api],
              ['OpenAPI', summary?.endpoints.openapi],
              ['MCP manifest', mcp?.endpoints.manifest || summary?.endpoints.mcp_manifest],
              ['MCP call', mcp?.endpoints.call || summary?.endpoints.mcp_call]
            ] as [string, string | undefined][]).map(([label, value]) => (
              <div
                key={label}
                className="flex items-center gap-3 rounded-lg border border-slate-200 p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-slate-500">{label}</p>
                  <p className="mt-0.5 truncate rounded bg-slate-900 px-2 py-1 font-mono text-xs text-green-400">
                    {value || '—'}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-icon shrink-0"
                  aria-label={`Copiar ${label}`}
                  onClick={() => copyToClipboard(String(value || ''), `${label} copiado.`)}
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {mcp && (
            <div className="mt-4 rounded-lg border border-slate-200 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">MCP — Configuração</p>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-slate-500">Autenticação</p>
                  <p className="mt-0.5 rounded bg-slate-900 px-3 py-2 font-mono text-xs text-green-400">{mcp.auth}</p>
                </div>
                {mcp.required_scopes.length > 0 && (
                  <div>
                    <p className="mb-1 text-xs text-slate-500">Escopos obrigatórios</p>
                    <div className="flex flex-wrap gap-1">
                      {mcp.required_scopes.map((scope) => (
                        <span key={scope} className="badge badge-info">{scope}</span>
                      ))}
                    </div>
                  </div>
                )}
                {mcp.optional_tool_scopes.length > 0 && (
                  <div>
                    <p className="mb-1 text-xs text-slate-500">Escopos opcionais</p>
                    <div className="flex flex-wrap gap-1">
                      {mcp.optional_tool_scopes.map((scope) => (
                        <span key={scope} className="badge">{scope}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
